import { CallAPI } from "../../apiManager/ApiCall";
import {
  demoRequestURL,
  demoRequestPublicURL,
  demoRequestAvailabilityURL,
  upcomingDemoRequestURL,
} from "apiManager/APIConfig";

/**
 * Admin: upcoming demos — GET /api/demo-request/upcoming
 * Returns `{ items, total, todayCount }`. Falls back to an empty list on
 * any error (e.g. 401/403 for non-admins) so the navbar badge can hide
 * itself silently.
 */
export const GetUpcomingDemoRequestsCall = async ({ limit = 25 } = {}) => {
  try {
    const response = await CallAPI({
      url: `${upcomingDemoRequestURL}?limit=${encodeURIComponent(limit)}`,
      method: "GET",
    });
    if (response?.success && Array.isArray(response?.items)) {
      return {
        items: response.items,
        total: Number(response.total || 0),
        todayCount: Number(response.today_count || 0),
      };
    }
    return { items: [], total: 0, todayCount: 0 };
  } catch (_) {
    return { items: [], total: 0, todayCount: 0 };
  }
};

/** Admin: list demo requests — GET /api/demo-request */
export const GetDemoRequestsCall = async () => {
  try {
    const response = await CallAPI({
      url: demoRequestURL,
      method: "GET",
    });
    if (response?.success && Array.isArray(response?.data)) {
      return {
        rows: response.data,
        statuses: Array.isArray(response.statuses) ? response.statuses : null,
      };
    }
    return { rows: [], statuses: null };
  } catch (error) {
    return { rows: [], statuses: null };
  }
};

/** Admin: POST /api/demo-request/:id/status (POST avoids CORS when edge allows only GET/POST/DELETE). */
export const UpdateDemoRequestStatusCall = async (id, status) => {
  try {
    const response = await CallAPI({
      url: `${demoRequestURL}/${id}/status`,
      method: "POST",
      body: { status },
    });
    return response?.success ? response : null;
  } catch (error) {
    return null;
  }
};

/** Admin: DELETE /api/demo-request/:id */
export const DeleteDemoRequestCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${demoRequestURL}/${id}`,
      method: "DELETE",
    });
    return response?.success ? response : null;
  } catch (error) {
    return null;
  }
};

/**
 * Public: schedule + already-booked slots — no auth required.
 * Powers the "Preferred demo times" step on the /request-demo page.
 *
 * Returns:
 *   {
 *     timezone:    "Europe/Dublin",
 *     weeksAhead:  3,
 *     schedule:    [{ weekday: "monday", times: ["16:00"] }, …],
 *     bookedSlots: [{ date: "2026-06-15", time: "16:00" }, …]
 *   }
 */
export const FetchDemoBookingAvailabilityCall = async () => {
  try {
    const response = await CallAPI({
      url: demoRequestAvailabilityURL,
      method: "GET",
    });
    return response && typeof response === "object" ? response : null;
  } catch (error) {
    return null;
  }
};

/**
 * Public: marketing-site "Request a demo" submission — no auth required.
 * The professor doesn't need a Microlab account yet; firstName/lastName/
 * universityName come from the form.
 *
 * On a slot conflict the backend returns 409 with { conflicts: [{date,time}…] }
 * — surface that to the user so they can pick again.
 */
export const SubmitPublicDemoRequestCall = async (body) => {
  try {
    const response = await CallAPI({
      url: demoRequestPublicURL,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    return { status: 0, message: "Network error." };
  }
};
